import { collection, addDoc, writeBatch, doc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Company, Lead, Opportunity, Task, User } from '../types';

/**
 * Service for generating comprehensive demo data for demo organizations
 */
export const demoDataService = {
  /**
   * Generate complete demo data for an organization
   */
  async generateDemoData(orgId: string, createdBy: string): Promise<void> {
    console.log(`🎬 Starting demo data generation for org: ${orgId}`);
    
    try {
      // Generate data in sequence to maintain relationships
      const companies = await this.generateCompanies(orgId, createdBy);
      console.log(`✅ Generated ${companies.length} companies`);
      
      const users = await this.generateDemoUsers(orgId, createdBy);
      console.log(`✅ Generated ${users.length} demo users`);
      
      const leads = await this.generateLeads(orgId, createdBy, companies, users);
      console.log(`✅ Generated ${leads.length} leads`);
      
      const opportunities = await this.generateOpportunities(orgId, createdBy, companies, leads, users);
      console.log(`✅ Generated ${opportunities.length} opportunities`);
      
      const tasks = await this.generateTasks(orgId, createdBy, leads, users);
      console.log(`✅ Generated ${tasks.length} tasks`);
      
      const activities = await this.generateActivities(orgId, createdBy, leads, users);
      console.log(`✅ Generated ${activities.length} activities`);
      
      const posts = await this.generatePosts(orgId, createdBy, users);
      console.log(`✅ Generated ${posts.length} posts`);
      
      console.log('🎉 Demo data generation completed successfully!');
    } catch (error) {
      console.error('❌ Error generating demo data:', error);
      throw error;
    }
  },

  /**
   * Generate demo companies
   */
  async generateCompanies(orgId: string, createdBy: string): Promise<string[]> {
    const companiesData = [
      {
        name: 'TechStartup Inc',
        domain: 'techstartup.com',
        industry: 'Technology',
        employeeCount: '50-100',
        revenue: '1–5 Mio. €',
        description: 'Innovative tech startup focusing on AI solutions for small and medium businesses',
        website: 'https://techstartup.com',
        phone: '+1 (555) 123-4567',
        address: {
          street: '123 Innovation Drive',
          city: 'San Francisco',
          state: 'CA',
          country: 'USA',
          postalCode: '94102'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/techstartup',
          twitter: '@techstartupinc'
        }
      },
      {
        name: 'RetailCorp Global',
        domain: 'retailcorp.com',
        industry: 'Retail',
        employeeCount: '200-500',
        revenue: '10–50 Mio. €',
        description: 'Leading retail company with nationwide presence and omnichannel strategy',
        website: 'https://retailcorp.com',
        phone: '+1 (555) 987-6543',
        address: {
          street: '456 Commerce Boulevard',
          city: 'Chicago',
          state: 'IL',
          country: 'USA',
          postalCode: '60601'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/retailcorp',
          facebook: 'facebook.com/retailcorp'
        }
      },
      {
        name: 'Finance Plus',
        domain: 'financeplus.com',
        industry: 'Finance',
        employeeCount: '500+',
        revenue: '50+ Mio. €',
        description: 'Premier financial services and investment firm with global reach',
        website: 'https://financeplus.com',
        phone: '+1 (555) 456-7890',
        address: {
          street: '789 Wall Street',
          city: 'New York',
          state: 'NY',
          country: 'USA',
          postalCode: '10005'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/financeplus',
          twitter: '@financeplusny'
        }
      },
      {
        name: 'EduTech Solutions',
        domain: 'edutech.org',
        industry: 'Education',
        employeeCount: '50-100',
        revenue: '1–5 Mio. €',
        description: 'Educational technology solutions for K-12 schools and universities',
        website: 'https://edutech.org',
        phone: '+1 (555) 321-0987',
        address: {
          street: '321 Learning Lane',
          city: 'Austin',
          state: 'TX',
          country: 'USA',
          postalCode: '78701'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/edutech',
          instagram: '@edutechsolutions'
        }
      },
      {
        name: 'HealthTech Innovations',
        domain: 'healthtech.io',
        industry: 'Healthcare',
        employeeCount: '100-200',
        revenue: '5–10 Mio. €',
        description: 'Healthcare technology solutions for hospitals and clinics',
        website: 'https://healthtech.io',
        phone: '+1 (555) 789-4561',
        address: {
          street: '555 Medical Center Dr',
          city: 'Boston',
          state: 'MA',
          country: 'USA',
          postalCode: '02108'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/healthtech'
        }
      },
      {
        name: 'GlobalTech Solutions',
        domain: 'globaltech.com',
        industry: 'Technology',
        employeeCount: '200-500',
        revenue: '25–50 Mio. €',
        description: 'Global technology consulting and implementation services',
        website: 'https://globaltech.com',
        phone: '+1 (555) 789-0123',
        address: {
          street: '100 Tech Plaza',
          city: 'Seattle',
          state: 'WA',
          country: 'USA',
          postalCode: '98101'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/globaltech'
        }
      },
      {
        name: 'Smart Logistics Co',
        domain: 'smartlogistics.co',
        industry: 'Logistics',
        employeeCount: '200-500',
        revenue: '20–50 Mio. €',
        description: 'Advanced logistics and supply chain management solutions',
        website: 'https://smartlogistics.co',
        phone: '+1 (555) 654-3210',
        address: {
          street: '999 Warehouse Way',
          city: 'Dallas',
          state: 'TX',
          country: 'USA',
          postalCode: '75201'
        },
        socialProfiles: { 
          linkedin: 'linkedin.com/company/smartlogistics'
        }
      }
    ];

    const companyIds: string[] = [];
    
    for (const companyData of companiesData) {
      const docRef = await addDoc(collection(db, 'companies'), {
        ...companyData,
        orgId,
        isDemo: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      companyIds.push(docRef.id);
    }
    
    return companyIds;
  },

  /**
   * Generate demo users for the organization
   */
  async generateDemoUsers(orgId: string, createdBy: string): Promise<string[]> {
    const usersData = [
      {
        name: 'Sarah Johnson',
        email: `sarah.johnson+demo_${orgId.substring(0, 8)}@demo.aideline.com`,
        role: 'sales_rep' as const,
        specialties: ['Technology', 'Software', 'SaaS'],
        position: 'Senior Sales Representative'
      },
      {
        name: 'Michael Chen',
        email: `michael.chen+demo_${orgId.substring(0, 8)}@demo.aideline.com`,
        role: 'manager' as const,
        specialties: ['Healthcare', 'Finance', 'Enterprise'],
        position: 'Sales Manager'
      },
      {
        name: 'Emma Williams',
        email: `emma.williams+demo_${orgId.substring(0, 8)}@demo.aideline.com`,
        role: 'sales_rep' as const,
        specialties: ['Education', 'Non-Profit', 'Government'],
        position: 'Sales Representative'
      },
      {
        name: 'David Rodriguez',
        email: `david.rodriguez+demo_${orgId.substring(0, 8)}@demo.aideline.com`,
        role: 'sales_rep' as const,
        specialties: ['Retail', 'E-commerce', 'Logistics'],
        position: 'Account Executive'
      }
    ];

    const userIds: string[] = [];
    
    for (const userData of usersData) {
      const docRef = await addDoc(collection(db, 'users'), {
        ...userData,
        orgId,
        isDemo: true,
        currentLeadCount: 0,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      userIds.push(docRef.id);
    }
    
    return userIds;
  },

  /**
   * Generate demo leads
   */
  async generateLeads(orgId: string, createdBy: string, companyIds: string[], userIds: string[]): Promise<string[]> {
    const now = Date.now();
    const leadsData = [
      {
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah@techstartup.com',
        company: 'TechStartup Inc',
        phone: '+1 (555) 123-4567',
        source: 'website',
        stage: 'interested',
        score: 85,
        assignedTo: userIds[0],
        nextFollowUp: new Date(now + 2 * 24 * 60 * 60 * 1000),
        title: 'CTO',
        notes: 'Very interested in enterprise solution. Budget of 50k+ €',
        tags: ['enterprise', 'hot-lead', 'technology']
      },
      {
        firstName: 'Michael',
        lastName: 'Brown',
        email: 'michael@retailcorp.com',
        company: 'RetailCorp Global',
        phone: '+1 (555) 987-6543',
        source: 'referral',
        stage: 'demo',
        score: 72,
        assignedTo: userIds[1],
        nextFollowUp: new Date(now + 1 * 24 * 60 * 60 * 1000),
        title: 'VP Sales',
        notes: 'Wants to see demo for retail use case',
        tags: ['retail', 'demo-scheduled']
      },
      {
        firstName: 'Emily',
        lastName: 'Davis',
        email: 'emily@healthtech.io',
        company: 'HealthTech Innovations',
        phone: '+1 (555) 789-4561',
        source: 'cold-outreach',
        stage: 'untouched',
        score: 45,
        assignedTo: userIds[2],
        title: 'CEO',
        tags: ['healthcare', 'cold-lead']
      },
      {
        firstName: 'David',
        lastName: 'Wilson',
        email: 'david@financeplus.com',
        company: 'Finance Plus',
        phone: '+1 (555) 456-7890',
        source: 'social',
        stage: 'offer',
        score: 91,
        assignedTo: userIds[0],
        nextFollowUp: new Date(now + 3 * 24 * 60 * 60 * 1000),
        title: 'CFO',
        notes: 'Ready to move forward. Waiting for board approval.',
        tags: ['finance', 'high-value', 'closing-soon']
      },
      {
        firstName: 'Lisa',
        lastName: 'Anderson',
        email: 'lisa@edutech.org',
        company: 'EduTech Solutions',
        phone: '+1 (555) 321-0987',
        source: 'website',
        stage: 'closing',
        score: 88,
        assignedTo: userIds[2],
        nextFollowUp: new Date(now + 1 * 24 * 60 * 60 * 1000),
        title: 'Founder & CEO',
        notes: 'Contract ready for signature. Just confirming start date.',
        tags: ['education', 'closing', 'hot-lead']
      },
      {
        firstName: 'Robert',
        lastName: 'Martinez',
        email: 'robert@globaltech.com',
        company: 'GlobalTech Solutions',
        phone: '+1 (555) 789-0123',
        source: 'referral',
        stage: 'demo',
        score: 92,
        assignedTo: userIds[1],
        nextFollowUp: new Date(now + 1 * 24 * 60 * 60 * 1000),
        title: 'CEO',
        notes: 'High potential. Interested in enterprise deployment.',
        tags: ['technology', 'enterprise', 'hot-lead']
      },
      {
        firstName: 'Amanda',
        lastName: 'Thompson',
        email: 'amanda@retailcorp.com',
        company: 'RetailCorp Global',
        phone: '+1 (555) 987-6544',
        source: 'website',
        stage: 'contacted',
        score: 65,
        assignedTo: userIds[3],
        title: 'Head of Digital',
        tags: ['retail', 'mid-priority']
      },
      {
        firstName: 'Kevin',
        lastName: 'Park',
        email: 'kevin@techstartup.com',
        company: 'TechStartup Inc',
        phone: '+1 (555) 123-4568',
        source: 'referral',
        stage: 'interested',
        score: 78,
        assignedTo: userIds[0],
        nextFollowUp: new Date(now + 2 * 24 * 60 * 60 * 1000),
        title: 'VP Engineering',
        tags: ['technology', 'technical-buyer']
      },
      {
        firstName: 'Jessica',
        lastName: 'Wright',
        email: 'jessica@financeplus.com',
        company: 'Finance Plus',
        phone: '+1 (555) 456-7891',
        source: 'social',
        stage: 'interested',
        score: 70,
        assignedTo: userIds[1],
        title: 'VP Operations',
        tags: ['finance', 'operations']
      },
      {
        firstName: 'Daniel',
        lastName: 'Rodriguez',
        email: 'daniel@smartlogistics.co',
        company: 'Smart Logistics Co',
        phone: '+1 (555) 654-3210',
        source: 'website',
        stage: 'demo',
        score: 82,
        assignedTo: userIds[3],
        nextFollowUp: new Date(now + 2 * 24 * 60 * 60 * 1000),
        title: 'COO',
        notes: 'Impressed with initial call. Wants detailed demo.',
        tags: ['logistics', 'operations', 'demo-scheduled']
      }
    ];

    const leadIds: string[] = [];
    
    for (const leadData of leadsData) {
      const docRef = await addDoc(collection(db, 'leads'), {
        ...leadData,
        orgId,
        isDemo: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      leadIds.push(docRef.id);
    }
    
    return leadIds;
  },

  /**
   * Generate demo opportunities
   */
  async generateOpportunities(
    orgId: string, 
    createdBy: string, 
    companyIds: string[], 
    leadIds: string[], 
    userIds: string[]
  ): Promise<string[]> {
    const now = Date.now();
    const opportunitiesData = [
      {
        title: 'TechStartup Inc - Enterprise CRM Implementation',
        stage: 'qualification',
        value: 50000,
        probability: 75,
        expectedCloseDate: new Date(now + 30 * 24 * 60 * 60 * 1000),
        assignedTo: userIds[0],
        source: 'inbound',
        priority: 'high',
        leadIds: [leadIds[0], leadIds[7]],
        description: 'Enterprise CRM rollout for tech startup. Main contact: Sarah Johnson (CTO)',
        tags: ['enterprise', 'technology', 'high-value']
      },
      {
        title: 'RetailCorp Global - Multi-location CRM Rollout',
        stage: 'proposal',
        value: 75000,
        probability: 65,
        expectedCloseDate: new Date(now + 45 * 24 * 60 * 60 * 1000),
        assignedTo: userIds[1],
        source: 'referral',
        priority: 'high',
        leadIds: [leadIds[1], leadIds[6]],
        description: '25 user deployment across multiple retail locations',
        tags: ['retail', 'multi-location', 'high-value']
      },
      {
        title: 'Finance Plus - Premium CRM Suite',
        stage: 'negotiation',
        value: 150000,
        probability: 85,
        expectedCloseDate: new Date(now + 15 * 24 * 60 * 60 * 1000),
        assignedTo: userIds[0],
        source: 'inbound',
        priority: 'high',
        leadIds: [leadIds[3], leadIds[8]],
        description: 'Enterprise financial services CRM with custom integrations',
        tags: ['finance', 'enterprise', 'premium']
      },
      {
        title: 'EduTech Solutions - Education CRM Platform',
        stage: 'closing',
        value: 35000,
        probability: 90,
        expectedCloseDate: new Date(now + 7 * 24 * 60 * 60 * 1000),
        assignedTo: userIds[2],
        source: 'inbound',
        priority: 'high',
        leadIds: [leadIds[4]],
        description: 'Educational CRM for K-12 technology provider. Contract ready.',
        tags: ['education', 'closing', 'hot-deal']
      },
      {
        title: 'GlobalTech Solutions - Enterprise Deployment',
        stage: 'qualification',
        value: 120000,
        probability: 70,
        expectedCloseDate: new Date(now + 60 * 24 * 60 * 60 * 1000),
        assignedTo: userIds[1],
        source: 'referral',
        priority: 'high',
        leadIds: [leadIds[5]],
        description: 'Large enterprise deployment for consulting firm',
        tags: ['technology', 'enterprise', 'consulting']
      },
      {
        title: 'Smart Logistics - Operations CRM',
        stage: 'proposal',
        value: 45000,
        probability: 60,
        expectedCloseDate: new Date(now + 40 * 24 * 60 * 60 * 1000),
        assignedTo: userIds[3],
        source: 'inbound',
        priority: 'medium',
        leadIds: [leadIds[9]],
        description: 'CRM for logistics operations and supply chain management',
        tags: ['logistics', 'operations']
      }
    ];

    const oppIds: string[] = [];
    
    for (const oppData of opportunitiesData) {
      const docRef = await addDoc(collection(db, 'opportunities'), {
        ...oppData,
        orgId,
        isDemo: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      oppIds.push(docRef.id);
    }
    
    return oppIds;
  },

  /**
   * Generate demo tasks
   */
  async generateTasks(orgId: string, createdBy: string, leadIds: string[], userIds: string[]): Promise<string[]> {
    const now = Date.now();
    const tasksData = [
      {
        title: 'Follow up with Sarah Johnson - Enterprise Pricing',
        description: 'Discuss enterprise pricing and implementation timeline',
        leadId: leadIds[0],
        assignedTo: userIds[0],
        dueDate: new Date(now + 1 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'high',
        type: 'call'
      },
      {
        title: 'Prepare RetailCorp Demo',
        description: 'Customize demo for retail use case with 25 users',
        leadId: leadIds[1],
        assignedTo: userIds[1],
        dueDate: new Date(now + 0.5 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'high',
        type: 'meeting'
      },
      {
        title: 'Send proposal to Finance Plus',
        description: 'Custom enterprise package pricing proposal',
        leadId: leadIds[3],
        assignedTo: userIds[0],
        dueDate: new Date(now),
        completed: false,
        priority: 'high',
        type: 'email'
      },
      {
        title: 'Contract signing - EduTech Solutions',
        description: 'Final contract signing and implementation planning',
        leadId: leadIds[4],
        assignedTo: userIds[2],
        dueDate: new Date(now),
        completed: false,
        priority: 'high',
        type: 'meeting'
      },
      {
        title: 'Research HealthTech Innovations',
        description: 'Gather intelligence and prepare outreach strategy',
        leadId: leadIds[2],
        assignedTo: userIds[2],
        dueDate: new Date(now - 1 * 24 * 60 * 60 * 1000),
        completed: true,
        priority: 'medium',
        type: 'follow_up'
      },
      {
        title: 'Executive meeting with GlobalTech CEO',
        description: 'Strategic discussion about enterprise CRM rollout',
        leadId: leadIds[5],
        assignedTo: userIds[1],
        dueDate: new Date(now + 2 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'high',
        type: 'meeting'
      },
      {
        title: 'Send ROI calculator to Michael Brown',
        description: 'Share ROI calculator and case studies for retail industry',
        leadId: leadIds[1],
        assignedTo: userIds[1],
        dueDate: new Date(now + 1 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'medium',
        type: 'email'
      },
      {
        title: 'Demo prep for Smart Logistics',
        description: 'Prepare logistics-specific demo and use cases',
        leadId: leadIds[9],
        assignedTo: userIds[3],
        dueDate: new Date(now + 1.5 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'medium',
        type: 'follow_up'
      },
      {
        title: 'Overdue: Follow up with David Wilson',
        description: 'Check on board approval status for Finance Plus deal',
        leadId: leadIds[3],
        assignedTo: userIds[0],
        dueDate: new Date(now - 2 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'high',
        type: 'call'
      },
      {
        title: 'Send welcome email to Kevin Park',
        description: 'Introduction email with technical overview',
        leadId: leadIds[7],
        assignedTo: userIds[0],
        dueDate: new Date(now + 3 * 24 * 60 * 60 * 1000),
        completed: false,
        priority: 'low',
        type: 'email'
      }
    ];

    const taskIds: string[] = [];
    
    for (const taskData of tasksData) {
      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        orgId,
        isDemo: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      taskIds.push(docRef.id);
    }
    
    return taskIds;
  },

  /**
   * Generate demo activities
   */
  async generateActivities(orgId: string, createdBy: string, leadIds: string[], userIds: string[]): Promise<string[]> {
    const now = Date.now();
    const activitiesData = [
      {
        type: 'email_sent',
        description: 'Welcome email sent to Sarah Johnson',
        leadId: leadIds[0],
        timestamp: new Date(now - 3 * 24 * 60 * 60 * 1000),
        userId: userIds[0]
      },
      {
        type: 'email_opened',
        description: 'Sarah Johnson opened welcome email',
        leadId: leadIds[0],
        timestamp: new Date(now - 2.5 * 24 * 60 * 60 * 1000),
        userId: userIds[0]
      },
      {
        type: 'email_reply',
        description: 'Reply received from Sarah Johnson - interested in demo',
        leadId: leadIds[0],
        timestamp: new Date(now - 2 * 24 * 60 * 60 * 1000),
        userId: userIds[0]
      },
      {
        type: 'call',
        description: 'Discovery call with Michael Brown - 30 minutes',
        leadId: leadIds[1],
        timestamp: new Date(now - 1 * 24 * 60 * 60 * 1000),
        userId: userIds[1]
      },
      {
        type: 'meeting',
        description: 'Product demo scheduled for RetailCorp team',
        leadId: leadIds[1],
        timestamp: new Date(now - 0.5 * 24 * 60 * 60 * 1000),
        userId: userIds[1]
      },
      {
        type: 'note',
        description: 'Added note: Very interested in multi-location capabilities',
        leadId: leadIds[1],
        timestamp: new Date(now - 0.3 * 24 * 60 * 60 * 1000),
        userId: userIds[1]
      },
      {
        type: 'proposal_sent',
        description: 'Enterprise proposal sent to David Wilson',
        leadId: leadIds[3],
        timestamp: new Date(now - 5 * 24 * 60 * 60 * 1000),
        userId: userIds[0]
      },
      {
        type: 'stage_change',
        description: 'Lead moved to Offer stage',
        leadId: leadIds[3],
        timestamp: new Date(now - 4 * 24 * 60 * 60 * 1000),
        userId: userIds[0]
      },
      {
        type: 'call',
        description: 'Follow-up call with David Wilson - board review status',
        leadId: leadIds[3],
        timestamp: new Date(now - 1 * 24 * 60 * 60 * 1000),
        userId: userIds[0]
      },
      {
        type: 'contract_sent',
        description: 'Final contract sent to Lisa Anderson',
        leadId: leadIds[4],
        timestamp: new Date(now - 2 * 24 * 60 * 60 * 1000),
        userId: userIds[2]
      },
      {
        type: 'stage_change',
        description: 'Lead moved to Closing stage',
        leadId: leadIds[4],
        timestamp: new Date(now - 1.5 * 24 * 60 * 60 * 1000),
        userId: userIds[2]
      },
      {
        type: 'meeting',
        description: 'Executive demo with Robert Martinez',
        leadId: leadIds[5],
        timestamp: new Date(now - 1 * 24 * 60 * 60 * 1000),
        userId: userIds[1]
      },
      {
        type: 'email_sent',
        description: 'Follow-up email with demo recording',
        leadId: leadIds[5],
        timestamp: new Date(now - 0.5 * 24 * 60 * 60 * 1000),
        userId: userIds[1]
      },
      {
        type: 'call',
        description: 'Initial outreach to Emily Davis - voicemail left',
        leadId: leadIds[2],
        timestamp: new Date(now - 3 * 24 * 60 * 60 * 1000),
        userId: userIds[2]
      },
      {
        type: 'email_sent',
        description: 'Cold email sent to Emily Davis',
        leadId: leadIds[2],
        timestamp: new Date(now - 2 * 24 * 60 * 60 * 1000),
        userId: userIds[2]
      }
    ];

    const activityIds: string[] = [];
    
    for (const activityData of activitiesData) {
      const docRef = await addDoc(collection(db, 'activities'), {
        ...activityData,
        orgId,
        isDemo: true,
        createdBy,
        createdAt: serverTimestamp()
      });
      activityIds.push(docRef.id);
    }
    
    return activityIds;
  },

  /**
   * Generate demo posts for the stream/feed
   */
  async generatePosts(orgId: string, createdBy: string, userIds: string[]): Promise<string[]> {
    const now = Date.now();
    const postsData = [
      {
        content: '🎉 Great news! Just closed a 150k € deal with Finance Plus. Their CFO was very impressed with our enterprise features. Thanks to the team for the support!',
        authorId: userIds[0],
        authorName: 'Sarah Johnson',
        type: 'achievement',
        likes: 12,
        timestamp: new Date(now - 2 * 60 * 60 * 1000)
      },
      {
        content: '📊 Weekly update: Successfully completed 8 demos this week with a 75% conversion to proposal stage. RetailCorp and GlobalTech looking very promising!',
        authorId: userIds[1],
        authorName: 'Michael Chen',
        type: 'update',
        likes: 8,
        timestamp: new Date(now - 5 * 60 * 60 * 1000)
      },
      {
        content: '💡 Tip: When dealing with education sector clients, emphasize compliance and data security features. Just had a great conversation with EduTech about FERPA compliance.',
        authorId: userIds[2],
        authorName: 'Emma Williams',
        type: 'tip',
        likes: 15,
        timestamp: new Date(now - 8 * 60 * 60 * 1000)
      },
      {
        content: '🎯 New quarter, new goals! Aiming for 500k € in pipeline this Q1. Who\'s with me?',
        authorId: userIds[3],
        authorName: 'David Rodriguez',
        type: 'motivation',
        likes: 10,
        timestamp: new Date(now - 12 * 60 * 60 * 1000)
      },
      {
        content: '📝 Just wrapped up a fantastic demo for Smart Logistics. They loved the automation features. Moving to proposal stage!',
        authorId: userIds[3],
        authorName: 'David Rodriguez',
        type: 'update',
        likes: 6,
        timestamp: new Date(now - 1 * 24 * 60 * 60 * 1000)
      },
      {
        content: '🚀 Product update: The new AI-powered lead scoring feature is amazing! Already seeing better prioritization of hot leads. Check it out!',
        authorId: userIds[1],
        authorName: 'Michael Chen',
        type: 'announcement',
        likes: 20,
        timestamp: new Date(now - 1.5 * 24 * 60 * 60 * 1000)
      },
      {
        content: '☕ Morning team! Remember to update your lead stages today. Let\'s keep our pipeline accurate and up-to-date.',
        authorId: userIds[1],
        authorName: 'Michael Chen',
        type: 'reminder',
        likes: 5,
        timestamp: new Date(now - 2 * 24 * 60 * 60 * 1000)
      },
      {
        content: '🎊 Congratulations to everyone who hit their targets last month! Special shoutout to Sarah for crushing it with 150% of quota!',
        authorId: userIds[1],
        authorName: 'Michael Chen',
        type: 'celebration',
        likes: 18,
        timestamp: new Date(now - 3 * 24 * 60 * 60 * 1000)
      }
    ];

    const postIds: string[] = [];
    
    for (const postData of postsData) {
      const docRef = await addDoc(collection(db, 'posts'), {
        ...postData,
        orgId,
        isDemo: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      postIds.push(docRef.id);
    }
    
    return postIds;
  }
};

